<template>
  <div class="lotto-game-buy">
    <div class="info-area" v-if="!(hideRank && lottoGame.rank == 0)">
      <div class="rank" :class="getRankClass(lottoGame.rank)">
        <template v-if="lottoGame.rank == 0">추첨전</template>
        <template v-else-if="lottoGame.rank == 6">낙첨</template>
        <template v-else>{{ lottoGame.rank }}등!!</template>
      </div>
    </div>

    <div class="ball-area">
      <div
        v-for="(ball, index) in filteredSortedDrwtWithWin"
        :key="index"
        class="ball-wrapper"
        :class="getBallClass(ball.number, ball.winCode)"
      >
        <div v-if="ball.isDummy" class="dummy-ball"></div>
        <div v-else class="not-dummy">
          <div v-if="ball.winCode === 2" class="bonus-indicator">+</div>
          <div class="ball" :class="getBallClass(ball.number, ball.winCode)">
            <div class="number">{{ ball.number }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="buy-type" :class="'bt-' + (lottoGame.buyType || 'TERRA')">
      <select
        v-if="editMode"
        class="buy-type-select"
        :value="lottoGame.buyType || 'TERRA'"
        @change="onTypeChange"
        @click.stop
      >
        <option value="TERRA">Breezer</option>
        <option value="AUTO">자동</option>
        <option value="MANUAL">수동</option>
        <option value="SEMI_AUTO">반자동</option>
      </select>
      <span v-else class="buy-type-label">{{ buyTypeLabel }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { LottoGameBuyResponse } from "@/lotto/class/index";

export default defineComponent({
  name: "LottoBuyGame",
  // props: { lottoGame: LottoGameBuyResponse },
  props: {
    lottoGame: {
      type: LottoGameBuyResponse,
      required: true,
    },
    onlyMatched: {
      type: Boolean,
      default: false,
    },
    hideRank: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update-buy-type"],
  setup() {
    return {};
  },

  data() {
    return {
      //
    };
  },
  mixins: [UtilMixin],
  computed: {
    drwtNumbers(): number[] {
      return [
        this.lottoGame.drwtNo1,
        this.lottoGame.drwtNo2,
        this.lottoGame.drwtNo3,
        this.lottoGame.drwtNo4,
        this.lottoGame.drwtNo5,
        this.lottoGame.drwtNo6,
      ];
    },

    // winDrwtNo1 ~ winDrwtNo6 배열로 정리
    winNumbers(): number[] {
      return [
        this.lottoGame.winDrwtNo1,
        this.lottoGame.winDrwtNo2,
        this.lottoGame.winDrwtNo3,
        this.lottoGame.winDrwtNo4,
        this.lottoGame.winDrwtNo5,
        this.lottoGame.winDrwtNo6,
      ];
    },
    // sortedDrwtWithWin(): { number: number; winCode: number }[] {
    //   const numbers = this.drwtNumbers.map((num, idx) => ({
    //     number: num,
    //     winCode: this.winNumbers[idx],
    //   }));

    //   // 보너스(winCode === 2)를 맨 뒤로 보냄
    //   return numbers.sort((a, b) => {
    //     if (a.winCode === 2) return 1;
    //     if (b.winCode === 2) return -1;
    //     return 0;
    //   });
    // },
    filteredSortedDrwtWithWin(): {
      number: number;
      winCode: number;
      isDummy: boolean;
    }[] {
      const allBalls = this.drwtNumbers.map((n, i) => ({
        number: n,
        winCode: this.winNumbers[i],
        isDummy: false,
      }));

      // onlyMatched 모드: 추첨 완료된 건에서 맞은 번호(winCode > 0)만 표시
      if (this.onlyMatched && this.isDraw()) {
        const matched = allBalls.filter((b) => b.winCode > 0);
        return matched.length > 0 ? matched : [{ number: 0, winCode: -1, isDummy: true }];
      }

      const normalBalls = allBalls.filter((b) => b.winCode !== 2);
      const bonusBall = allBalls.find((b) => b.winCode === 2);
      const dummyBall = { number: 0, winCode: -1, isDummy: true };

      return bonusBall ? [...normalBalls, bonusBall] : [...normalBalls, dummyBall];
    },
    buyTypeLabel(): string {
      const map: Record<string, string> = { TERRA: "Breezer", AUTO: "자동", MANUAL: "수동", SEMI_AUTO: "반자동" };
      return map[this.lottoGame.buyType] || "Breezer";
    },
  },
  methods: {
    onTypeChange(e: Event) {
      const value = (e.target as HTMLSelectElement).value;
      this.$emit("update-buy-type", { id: this.lottoGame.id, buyType: value });
    },
    /**
     * lottoGame 결과 여부 확인
     */
    isDraw(): boolean {
      return this.lottoGame.isDraw();
    },
    getBallClass(drwtNo: number | null, winDrwtNo: number) {
      if (drwtNo === null) {
        return "bg-black-400";
      }
      if (this.isDraw()) {
        if (winDrwtNo > 0) {
          return this.getColorClass(drwtNo); // 당첨 번호라면 특정 색상
        }
        return "bg-black-400"; // 당첨되지 않은 경우 회색
      } else {
        return this.getColorClass(drwtNo); // 당첨 번호라면 특정 색상
      }
    },

    /**
     * 숫자 범위에 따라 색상을 반환하는 함수
     */
    getColorClass(number: number) {
      if (number >= 1 && number <= 10) return "bg-yellow-400";
      if (number > 10 && number <= 20) return "bg-blue-400";
      if (number > 20 && number <= 30) return "bg-red-400";
      if (number > 30 && number <= 40) return "bg-gray-400";
      if (number > 40 && number <= 45) return "bg-green-400";
      return "bg-black-400"; // 예외처리
    },
    getRankClass(rank: number): string {
      switch (rank) {
        case 0:
          return "gray"; // 미추첨
        case 1:
          return "orange"; // 1등
        case 2:
          return "purple"; // 2등
        case 3:
          return "blue"; // 3등
        case 4:
          return "green"; // 4등
        case 5:
          return "black"; // 5등
        case 6:
          return "light-gray"; // 낙첨
        default:
          return ""; // 기본값 (필요시 추가)
      }
    },
    calcWin() {
      if (this.isDraw()) {
        //
      }
    },
  },
  created() {
    this.calcWin();
  },
});
</script>

<style scoped>
.buy-type {
  display: flex;
  justify-content: center;
}
.buy-type-label {
  font-size: 0.8rem;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.18);
  color: #94a3b8;
  white-space: nowrap;
  letter-spacing: 0.01em;
}
.bt-TERRA .buy-type-label {
  background: color-mix(in oklab, var(--t-brand) 18%, transparent);
  color: var(--t-brand);
}
.bt-AUTO .buy-type-label {
  background: color-mix(in oklab, var(--t-color-success) 18%, transparent);
  color: var(--t-color-success);
}
.bt-MANUAL .buy-type-label {
  background: color-mix(in oklab, var(--t-lotto-accent) 20%, transparent);
  color: var(--t-lotto-accent);
}
.bt-SEMI_AUTO .buy-type-label {
  background: color-mix(in oklab, #a855f7 22%, transparent);
  color: #b57cf0;
}
.buy-type-select {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid var(--t-border-1, #cbd5e1);
  background: var(--t-bg-1, #fff);
  color: var(--t-text-1, #1e293b);
  cursor: pointer;
}
</style>
